import { ChangeDetectionStrategy, Component, ElementRef, Input, inject } from '@angular/core';

import { NgStyle } from '@angular/common';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { Customer } from '@df/session/api/customer';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ResultComponent } from 'projects/app-default/src/app/ui/resut/result.component';
import { TooltipComponent } from '../../../../../../ui/common/tooltip/tooltip.component';
import { NBuilderConfig } from '../builder.config';
import { OutfitSaveService } from './outfit-save.service';

@Component({
    selector: 'outfit-save-button',
    templateUrl: './outfit-save-button.component.html',
    styleUrls: ['./outfit-save-button.component.scss'],
    standalone: true,
    imports: [TranslationModule, TooltipComponent, ResultComponent, NgStyle],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutfitSaveButtonComponent extends UiComponent {
    @Input({ required: true })
    storageData!: NBuilderConfig.IStorageData;

    protected saveStatus = new Status();
    protected currentSavedHash?: string;
    protected isCurrentOutfitSaved = false;

    private hashStorageService = inject(HashStorageService);
    private outfitSaveService = inject(OutfitSaveService);
    private events = inject(Events);
    private pwaIntegrationService = inject(PwaIntegrationService);
    private customer = inject(Customer);
    private elementRef = inject(ElementRef);

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.events.on(NBuilderConfig.updateStorageEvent).subscribe(() => {
                this.isCurrentOutfitSaved = this.currentSavedHash === this.hashStorageService.getHash();
                this.detectChanges();
            })
        );
        this.subscriptions.add(
            this.saveStatus.subscribe(() => {
                this.detectChanges();
            })
        );
    }

    protected async saveOutfit(): Promise<void> {
        if (this.saveStatus.busy) return;

        try {
            // Reset status before starting
            this.saveStatus.reset();
            this.saveStatus.busy = true;
            this.changeDetectorRef.detectChanges();

            // Get current outfit hash from URL
            this.currentSavedHash = this.hashStorageService.getHash();
            if (!this.currentSavedHash) {
                throw new Error('No outfit configuration found');
            }

            // Find the gallery element in the DOM
            const galleryElement = this.findGalleryElement();

            // Save or update outfit with gallery image
            await this.outfitSaveService.saveOutfit(this.storageData, this.saveStatus, galleryElement);

            // Only mark as saved if there was no error
            if (!this.saveStatus.error) {
                this.isCurrentOutfitSaved = true;
            }
            this.detectChanges();
        } catch (error) {
            console.error('Error saving outfit:', error);
            this.saveStatus.error = error instanceof Error ? error.message : 'Failed to save outfit';
        }
    }

    /**
     * Find the gallery element in the DOM
     * @returns The gallery element or null if not found
     */
    private findGalleryElement(): HTMLElement | undefined {
        try {
            // Look for the builder-gallery element in the document
            // Since the save button and gallery are in the same parent component
            const galleryElement = document.querySelector('builder-gallery ._image-section') as HTMLElement;

            if (galleryElement) {
                return galleryElement;
            }

            // Fallback: look for the gallery element by traversing up from the save button
            let currentElement = this.elementRef.nativeElement.parentElement;
            while (currentElement) {
                const gallery = currentElement.querySelector('builder-gallery ._image-section') as HTMLElement;
                if (gallery) {
                    return gallery;
                }
                currentElement = currentElement.parentElement;
            }

            console.warn('Gallery element not found for outfit image capture');
            return undefined;
        } catch (error) {
            console.error('Error finding gallery element:', error);
            return undefined;
        }
    }

    protected getSaveIconClass(): string {
        if (this.saveStatus.busy) {
            return 'icon-loading';
        }
        return this.isCurrentOutfitSaved ? 'icon-tick' : 'icon-save';
    }

    protected getSaveTooltipText(): string {
        if (this.isCurrentOutfitSaved) {
            return 'Outfit Saved';
        }
        return 'Save Outfit';
    }

    protected getSaveSuccessText(): string {
        return 'Outfit saved!';
    }

    protected get isVisible(): boolean {
        // Only show save button for logged-in users in PWA mode
        return !!this.pwaIntegrationService.isStandalone && !!this.customer.model.isLoggedIn;
    }
}
